import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import {
  Box,
  Button,
  ClickAwayListener,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import * as R from 'ramda';

// Components
import { DrawerLayoutWrapper, MDialog } from 'common';

// Context
import { AuthContext } from 'authentication/AuthContext';

// Steps
import FulfillmentStep from './fulfillment/Fulfillment';
import DetailsPanel from './details-panel/DetailsPanel';
import OrderDetails from './order-details/OrderDetails';
import SelectOrigins from './select-origins/SelectOrigins';
import SelectCustomerChain from './select-customer-chain/SelectCustomerChain';
import ConfirmAndSubmit from './confirm-and-submit/ConfirmAndSubmit';
import OrderComplete from './order-complete/OrderComplete';

// Styles
import InterAffiliateDialog from './InterAffiliateDialog';

const NUM_CREATE_PTO_WIZARD_PAGES = 6;

const CreatePtoWizard = ({
  onClose,
  createPtoData,
  currentAffiliate,
  affiliateOptions,
  isCopyToNew,
  copyPtoNumber,
  fetchAffiliates,
  fetchCopyPto,
  fetchOriginatorLocationProductBalances,
  fetchOriginatorLocationProductOrigins,
  fetchOriginatorLocationProductReidVaporPressures,
  fetchOriginatorLocationProducts,
  fetchOriginatorLocations,
  fetchRestrictedLocations,
  restrictedLocationsPending,
  originatorLocations,
  originatorLocationOptions,
  originatorLocationProducts,
  originatorLocationProductsPending,
  originatorLocationProductsOptions,
  originatorLocationProductReidVaporPressuresOptions,
  originatorLocationProductReidVaporPressuresPending,
  originatorLocationProductOrigins,
  originatorLocationProductOriginsPending,
  originatorLocationProductBalances,
  originatorLocationProductBalancesPending,
  refreshCall,
  resetOriginatorLocationProductReidVaporPressures,
  restrictedLocations,
  sendCreatePto,
  sendError,
  sendPending,
  updateCreatePtoField,
}) => {
  const theme = useTheme();

  const {
    anyOrigin,
    chainContactIds,
    chained,
    dataCompanyId,
    locationId,
    originatorIdName,
    originIds,
    productId,
    recipientIdName,
    rvp,
    volume,
    transferDate,
  } = createPtoData;

  const [initialState, setInitialState] = useState({ ...createPtoData, transferDate: null });
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [allowContinue, setAllowContinue] = useState(false);
  const [openInterAffiliateDialog, setOpenInterAffiliateDialog] = useState(false);
  const totalPageCount = chained ? NUM_CREATE_PTO_WIZARD_PAGES : NUM_CREATE_PTO_WIZARD_PAGES - 1;
  const [initialLoad, setInitialLoad] = useState(true);

  const { affiliates } = useContext(AuthContext);

  const [volumeValidation, setVolumeValidation] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);

  // these fetches are required for the subsequent step
  // pending fetch control for SelectOrigins
  const selectOriginTransitionFetch =
    originatorLocationProductBalancesPending || restrictedLocationsPending;

  // all transition fetches combined, when these are false, the interactive CircularProgress spinner will not be rendered
  const transitionPending =
    // hold on OrderDetails until we are sure origins fetch is completed
    (currentPageNumber === 2 && originatorLocationProductOriginsPending) ||
    // hold on SelectOrigins until fetches are complete
    selectOriginTransitionFetch ||
    // hold on creating pto
    sendPending;

  const isStepThreeVolumeValid = R.cond([
    [() => currentPageNumber !== 3, R.T],
    [
      R.T,
      () =>
        R.allPass([
          R.pipe(R.prop('volume'), R.complement(R.isNil)),
          R.pipe(R.prop('volume'), R.complement(R.isEmpty)),
          () => R.isEmpty(volumeValidation),
        ])(createPtoData),
    ],
  ])();

  // each step that has user input should utilize this function to halt continuing
  // this could be for waiting for form validation or api calls
  const handleSetAllowContinue = value => setAllowContinue(value);

  // OrderDetails step controls:
  // conditional that controls if we are on OrderDetails step
  const displayOrderDetails =
    currentPageNumber === 1 || (currentPageNumber === 2 && originatorLocationProductOriginsPending);

  // this useEffect hook controls when the user can continue when on OrderDetails
  useEffect(() => {
    // Only apply Step 1 validation rules when on Step 1
    if (displayOrderDetails) {
      const value = !!(locationId && productId && recipientIdName && transferDate);
      handleSetAllowContinue(value && !originatorLocationProductReidVaporPressuresPending);

      if (value && !originatorLocationProductReidVaporPressuresPending && isInitialLoad) {
        fetchOriginatorLocationProductReidVaporPressures({
          locationId,
          originator: {
            companyCustomerIdName: originatorIdName,
            dataCompanyId,
          },
          productId,
        });
        setIsInitialLoad(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayOrderDetails,
    locationId,
    originatorLocationProductReidVaporPressuresPending,
    productId,
    recipientIdName,
    transferDate,
  ]);
  const handleSetLocation = locationIdArg => {
    if (originatorLocationProductsPending) {
      // need to revert page number if user has hit continue, but changed their mind
      if (currentPageNumber === 2) setCurrentPageNumber(1);
    }

    if (locationIdArg) {
      fetchOriginatorLocationProducts({
        locationId: locationIdArg,
        originator: {
          companyCustomerIdName: originatorIdName,
          dataCompanyId,
        },
      });
    }
  };

  const handleSetProduct = productIdArg => {
    // whenever a product is chosen or changed, need to wait for rvp call to resolve before continuing
    updateCreatePtoField('rvp', null);
    // cancel pending fetch of rvp options, if necessary
    if (originatorLocationProductReidVaporPressuresPending) {
      // need to revert page number if user has hit continue, but changed their mind
      if (currentPageNumber === 2) setCurrentPageNumber(1);
    }

    if (originatorLocationProductOriginsPending) {
      // need to revert page number if user has hit continue, but changed their mind
      if (currentPageNumber === 2) setCurrentPageNumber(1);
    }

    if (productIdArg) {
      // create new cancel token
      fetchOriginatorLocationProductReidVaporPressures({
        locationId,
        originator: {
          companyCustomerIdName: originatorIdName,
          dataCompanyId,
        },
        productId: productIdArg,
      });

      fetchOriginatorLocationProductOrigins({
        locationId,
        originator: {
          companyCustomerIdName: originatorIdName,
          dataCompanyId,
        },
        productId: productIdArg,
      });
    }
  };

  const handleSetRvp = rvpArg => {
    // this cancellation will occur often, the user will likely select an rvp right after selecting a product
    if (originatorLocationProductOriginsPending) {
      // need to revert page number if user has hit continue, but changed their mind
      if (currentPageNumber === 2) setCurrentPageNumber(1);
    }

    fetchOriginatorLocationProductOrigins({
      locationId,
      originator: {
        companyCustomerIdName: originatorIdName,
        dataCompanyId,
      },
      productId,
      rvp: rvpArg,
    });
  };

  // SelectOrigins:
  // conditional that controls if we are on SelectOrigins step
  const displaySelectOrigins = currentPageNumber === 2 && !originatorLocationProductOriginsPending;

  const handleSetOrigins = async () => {
    // disable continue button until the following call completes
    setAllowContinue(false);

    // set fetch to return a promise that will be waited on later
    const fetchOnePromise = fetchOriginatorLocationProductBalances({
      locationId,
      originIds: anyOrigin ? null : originIds,
      originator: {
        companyCustomerIdName: originatorIdName,
        dataCompanyId,
      },
      productId,
      rvp,
    });

    // set fetch to return a promise that will be waited on later
    const fetchTwoPromise = fetchRestrictedLocations({
      affiliate: {
        companyCustomerIdName: originatorIdName,
        dataCompanyId,
      },
      locationId,
      origins: originIds,
      productId,
    });

    // wait on above promises to resolve
    const fetchSuccessArr = await Promise.all([fetchOnePromise, fetchTwoPromise]);

    // test to see if above promises were successful to change page
    if (fetchSuccessArr.every(fetch => fetch)) setCurrentPageNumber(currentPageNumber + 1);

    // allow the user to continue whether the fetch was successful or not
    setAllowContinue(true);
  };

  // ConfirmAndSubmit
  const onSummary = currentPageNumber === totalPageCount;
  const displayConfirmAndSubmit = currentPageNumber === totalPageCount - 1;

  const handleCreateNew = () => {
    Object.keys(initialState).forEach(key => {
      updateCreatePtoField(key, initialState[key]);
    });

    setInitialState({ ...initialState });
    setVolumeValidation('');
    setCurrentPageNumber(1);
    setAllowContinue(false);
    resetOriginatorLocationProductReidVaporPressures();
  };

  const goToNextPage = () => {
    // call balances endpoint for default origin setting on page render
    if (currentPageNumber === 2) {
      handleSetOrigins();
      return;
    }
    if (currentPageNumber < totalPageCount) {
      setCurrentPageNumber(currentPageNumber + 1);
    }
    if (initialLoad && currentPageNumber === 1) {
      updateCreatePtoField('originIds', []);
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNumber === 2) {
      setInitialLoad(false);
      setAllowContinue(originIds.length > 0);
    }
    if (currentPageNumber === 3) {
      setAllowContinue(true);
    }
    if (currentPageNumber === 4 && chained) {
      setAllowContinue(true);
    }
    if (currentPageNumber > 1) {
      setCurrentPageNumber(currentPageNumber - 1);
    }
  };

  const submit = async () => {
    openInterAffiliateDialog && setOpenInterAffiliateDialog(false);
    // remove commas from volume before submitting
    const cleanData = {
      ...createPtoData,
      volume: R.pipe(R.prop('volume'), R.replace(/,/g, ''))(createPtoData),
    };
    await sendCreatePto(cleanData);
    refreshCall();
    goToNextPage();
  };

  const handleSubmitCreatePto = () => {
    const interAffiliateOrder = R.any(R.propEq('customerIdName', recipientIdName))(affiliates);
    if (interAffiliateOrder) {
      return setOpenInterAffiliateDialog(true);
    }
    return submit();
  };

  useEffect(() => {
    if (copyPtoNumber) {
      fetchCopyPto({ productTransferOrderId: copyPtoNumber });
    }
  }, [copyPtoNumber, fetchCopyPto]);

  useEffect(() => {
    if (dataCompanyId) {
      fetchAffiliates({
        dataCompanyId,
        orderBy: 'frequency',
      });
    }
  }, [dataCompanyId, fetchAffiliates]);

  useEffect(() => {
    if (dataCompanyId)
      fetchOriginatorLocations({
        orderBy: 'frequency',
        originator: {
          companyCustomerIdName: originatorIdName,
          dataCompanyId,
        },
      });
  }, [dataCompanyId, fetchOriginatorLocations, originatorIdName]);

  useEffect(() => {
    // reset selected rvp anytime productId changes
    // reset rvp options when product is cleared
    if (!productId) resetOriginatorLocationProductReidVaporPressures();
  }, [productId, resetOriginatorLocationProductReidVaporPressures, updateCreatePtoField]);

  const showBackBtn = !displayOrderDetails && currentPageNumber !== totalPageCount;

  const buttons = (
    <Box alignItems="center" display="flex" justifyContent="center" width="100%">
      {showBackBtn && (
        <Button
          disabled={sendPending}
          fullWidth={currentPageNumber !== totalPageCount}
          onClick={() => {
            goToPreviousPage();
          }}
          sx={{ mr: 2 }}
          variant="outlined"
        >
          Back
        </Button>
      )}
      {currentPageNumber < totalPageCount - 1 && (
        <Button
          disabled={!allowContinue || transitionPending || !isStepThreeVolumeValid}
          fullWidth={showBackBtn}
          onClick={goToNextPage}
        >
          Continue
        </Button>
      )}
      {displayConfirmAndSubmit && (
        <Button
          disabled={sendPending}
          fullWidth={displayConfirmAndSubmit}
          onClick={() => {
            handleSubmitCreatePto();
          }}
        >
          Submit Order
        </Button>
      )}
      {onSummary && (
        <Button fullWidth={onSummary} onClick={handleCreateNew} sx={{ mr: 2 }} variant="outlined">
          Create New
        </Button>
      )}
      {onSummary && (
        <Button fullWidth={onSummary} onClick={onClose}>
          Done
        </Button>
      )}
      {transitionPending && (
        <CircularProgress
          size={14}
          sx={{
            bottom: 0,
            color: 'secondary.main',
            left: 0,
            margin: 'auto',
            pointerEvents: 'none',
            position: 'absolute',
            right: 0,
            top: 0,
          }}
        />
      )}
    </Box>
  );

  let currentLocation;
  let currentProduct;

  if (originatorLocations)
    currentLocation = originatorLocations.find(location => location.locationId === locationId);
  if (originatorLocationProducts)
    currentProduct = originatorLocationProducts.find(product => product.productId === productId);

  const steps = () =>
    createPtoData.chained
      ? [
          {
            id: 1,
            stepLabel: 'Details',
          },
          {
            id: 2,
            stepLabel: 'Origin',
          },
          {
            id: 3,
            stepLabel: 'Fulfillment',
          },
          {
            id: 4,
            stepLabel: 'Chain',
          },
          {
            id: 5,
            stepLabel: 'Summary',
          },
        ]
      : [
          {
            id: 1,
            stepLabel: 'Details',
          },
          {
            id: 2,
            stepLabel: 'Origin',
          },
          {
            id: 3,
            stepLabel: 'Fulfillment',
          },
          {
            id: 4,
            stepLabel: 'Summary',
          },
        ];

  const getLabel = () => steps().find(({ id }) => id === currentPageNumber)?.stepLabel;
  const progress = (currentPageNumber / steps().length) * 100;

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={() => {
        if (!R.equals(initialState, createPtoData) && !onSummary) {
          setOpenDialog(true);
        } else {
          onClose();
        }
      }}
      touchEvent="onTouchStart"
    >
      <Box sx={{ height: '100%' }}>
        <DrawerLayoutWrapper
          buttons={buttons}
          headerButtonText="CLOSE"
          onHeaderButtonClick={() => {
            if (!R.equals(initialState, createPtoData) && !onSummary) {
              setOpenDialog(true);
            } else {
              onClose();
            }
          }}
          title={isCopyToNew ? 'Copy to New Order' : 'New Order'}
        >
          <>
            {!onSummary && (
              <Box
                sx={{
                  borderBottom: `0.1em solid ${theme.palette.graphic.light}`,
                  display: 'flex',
                  flexDirection: 'column',
                  flexShrink: 0,
                  height: 84,
                  justifyContent: 'center',
                  px: 3,
                  w: 1,
                }}
              >
                <LinearProgress
                  data-variant="magellanProgressBar"
                  value={progress}
                  variant="determinate"
                />
                <Typography variant="caption">{`${getLabel()} (${currentPageNumber} of ${
                  steps().length
                })`}</Typography>
              </Box>
            )}
            {displayOrderDetails && (
              <OrderDetails
                affiliateOptions={affiliateOptions}
                affiliates={affiliates}
                data={createPtoData}
                initialState={initialState}
                locationOptions={originatorLocationOptions}
                onSetLocation={handleSetLocation}
                onSetProduct={handleSetProduct}
                onSetRvp={handleSetRvp}
                products={originatorLocationProductsOptions}
                productsOptionsLoading={originatorLocationProductsPending}
                reidVaporPressures={originatorLocationProductReidVaporPressuresOptions}
                rvp={rvp}
                setAllowContinue={handleSetAllowContinue}
                setInitialLoad={setInitialLoad}
                setInitialState={setInitialState}
                setValue={updateCreatePtoField}
              />
            )}
            {displaySelectOrigins && (
              <SelectOrigins
                data={createPtoData}
                initialLoad={initialLoad}
                origins={originatorLocationProductOrigins}
                setAllowContinue={handleSetAllowContinue}
                setValue={updateCreatePtoField}
              />
            )}
            {currentPageNumber === 3 && (
              <FulfillmentStep
                balances={originatorLocationProductBalances}
                data={createPtoData}
                restrictedLocations={restrictedLocations}
                setAllowContinue={handleSetAllowContinue}
                setValue={updateCreatePtoField}
                setVolumeValidation={setVolumeValidation}
                volume={volume}
                volumeValidation={volumeValidation}
              />
            )}
            {currentPageNumber === 4 && createPtoData.chained && (
              <SelectCustomerChain
                affiliateOptions={affiliateOptions}
                data={createPtoData}
                onAllRequiredFieldsSet={handleSetAllowContinue}
                setValue={updateCreatePtoField}
              />
            )}
            {displayConfirmAndSubmit && (
              <ConfirmAndSubmit
                affiliateOptions={affiliateOptions}
                affiliates={affiliates}
                chainContactIds={chainContactIds}
                currentAffiliate={currentAffiliate}
                currentLocation={currentLocation}
                currentProduct={currentProduct}
                data={createPtoData}
                originatorLocationProductOrigins={originatorLocationProductOrigins}
              />
            )}
            {onSummary && (
              <OrderComplete
                affiliateOptions={affiliateOptions}
                affiliates={affiliates}
                chainContactIds={chainContactIds}
                currentAffiliate={currentAffiliate}
                currentLocation={currentLocation}
                currentProduct={currentProduct}
                data={createPtoData}
                onCreateAnotherOrderClick={onClose}
                onDoneClick={onClose}
                originatorLocationProductOrigins={originatorLocationProductOrigins}
                submitError={sendError}
              />
            )}
          </>
          {displayOrderDetails || displayConfirmAndSubmit || onSummary ? null : (
            <DetailsPanel
              affiliateOptions={affiliateOptions}
              affiliates={affiliates}
              chainContactIds={chainContactIds}
              currentAffiliate={currentAffiliate}
              currentLocation={currentLocation}
              currentProduct={currentProduct}
              data={createPtoData}
              originatorLocationProductOrigins={originatorLocationProductOrigins}
              originatorLocationProducts={originatorLocationProducts}
              originatorLocations={originatorLocations}
            />
          )}
          {openInterAffiliateDialog && (
            <InterAffiliateDialog
              onClose={() => setOpenInterAffiliateDialog(false)}
              submit={submit}
            />
          )}
          <MDialog
            header="Unsaved Changes"
            leftButtonAction={() => {
              setOpenDialog(false);
              onClose();
            }}
            leftButtonText="Continue"
            onClose={() => false}
            open={openDialog}
            rightButtonAction={() => setOpenDialog(false)}
            rightButtonText="Keep Editing"
          >
            <Typography color="textPrimary" component="span" variant="subtitle1">
              Are you sure you want to leave this page? You have unsaved PTO changes.
            </Typography>
          </MDialog>
        </DrawerLayoutWrapper>
      </Box>
    </ClickAwayListener>
  );
};

CreatePtoWizard.propTypes = {
  affiliateOptions: PropTypes.arrayOf(PropTypes.shape({})),
  copyPtoNumber: PropTypes.number,
  createPtoData: PropTypes.shape({
    anyOrigin: PropTypes.bool,
    chainContactIds: PropTypes.arrayOf(PropTypes.number),
    chained: PropTypes.bool,
    dataCompanyId: PropTypes.number,
    locationId: PropTypes.number,
    originIds: PropTypes.arrayOf(PropTypes.number),
    originatorIdName: PropTypes.string,
    productId: PropTypes.number,
    recipientIdName: PropTypes.string,
    rvp: PropTypes.number,
    transferDate: PropTypes.string,
    volume: PropTypes.string,
  }).isRequired,
  currentAffiliate: PropTypes.shape({}),
  fetchAffiliates: PropTypes.func.isRequired,
  fetchCopyPto: PropTypes.func.isRequired,
  fetchOriginatorLocationProductBalances: PropTypes.func.isRequired,
  fetchOriginatorLocationProductOrigins: PropTypes.func.isRequired,
  fetchOriginatorLocationProductReidVaporPressures: PropTypes.func.isRequired,
  fetchOriginatorLocationProducts: PropTypes.func.isRequired,
  fetchOriginatorLocations: PropTypes.func.isRequired,
  fetchRestrictedLocations: PropTypes.func.isRequired,
  isCopyToNew: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  originatorLocationOptions: PropTypes.arrayOf(PropTypes.shape({})),
  originatorLocationProductBalances: PropTypes.shape({}).isRequired,
  originatorLocationProductBalancesPending: PropTypes.bool.isRequired,
  originatorLocationProductOrigins: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  originatorLocationProductOriginsPending: PropTypes.bool.isRequired,
  originatorLocationProductReidVaporPressuresOptions: PropTypes.arrayOf(PropTypes.shape({}))
    .isRequired,
  originatorLocationProductReidVaporPressuresPending: PropTypes.bool.isRequired,
  originatorLocationProducts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  originatorLocationProductsOptions: PropTypes.arrayOf(PropTypes.shape({})),
  originatorLocationProductsPending: PropTypes.bool.isRequired,
  originatorLocations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  refreshCall: PropTypes.func.isRequired,
  resetOriginatorLocationProductReidVaporPressures: PropTypes.func.isRequired,
  restrictedLocations: PropTypes.arrayOf(PropTypes.shape({})),
  restrictedLocationsPending: PropTypes.bool.isRequired,
  sendCreatePto: PropTypes.func.isRequired,
  sendError: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]).isRequired,
  sendPending: PropTypes.bool.isRequired,
  updateCreatePtoField: PropTypes.func.isRequired,
};

CreatePtoWizard.defaultProps = {
  affiliateOptions: [],
  copyPtoNumber: null,
  currentAffiliate: undefined,
  originatorLocationOptions: [],
  originatorLocationProductsOptions: [],
  restrictedLocations: [],
};

export default compose()(CreatePtoWizard);
